import {useEffect, useState} from "react";
import styled from "styled-components";
import {queryClient} from "..";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {scheduleState} from "../app/reducers/schedule-slice";
import DateRangePicker, {DateRangeState,} from "../components/date/DateRangePicker";
import {DeleteScheduleAllDialogContainer} from "../components/dialog/DeleteScheduleAllDialogContainer";
import {DeliveryGroupDialog} from "../components/dialog/DeliveryGroupDialog";
import {PageController} from "../components/paging/PageController";
import {BasicSingleSelector} from "../components/selector/BasicSingleSelector";
import {SearchSchedulesInPeriodTable} from "../components/table/SearchSchedulesInPeriodTable";
import {IconTextField} from "../components/text-field/IconTextField";
import {ScheduleQueryKey, useGetSchedulePageByPeriodQuery,} from "../query/schedule-query";
import theme from "../style/theme";
import {useDebouncedCallback} from "../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";
import {dateFormat} from "../utils/date/date-format";
import {startGlobalLoadingAction} from "../app/reducers/loading-slice";
import EventBus, {EventTypes} from "../utils/eventbus/event-bus";
import useEventBus from "../utils/eventbus/use-event-bus";

const searchTabMap: { [key: string]: string } = {
  workerName: "프로님",
  clientName: "고객사",
  agencyName: "대행사",
};

const placeholderMap: { [key: string]: string } = {
  workerName: "프로님 이름을 입력하세요",
  clientName: "고객사을 입력하세요",
  agencyName: "대행사를 입력하세요",
};

export const ScheduleCheck = () => {
  const dispatch = useAppDispatch();
  const { schedulePage } = useAppSelector(scheduleState);
  // const { profile } = useAppSelector(profileState);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [pagePayload, setPagePayload] = useState<any>({});
  const [dateRange, setDateRange] = useState<DateRangeState[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [selectedSearchTabStatus, setSelectedSearchTabStatus] =
    useState<string>("workerName");
  const [deleteScheduleAllDialogOpen, setDeleteScheduleAllDialogOpen] =
    useState(false);
  useGetSchedulePageByPeriodQuery(pagePayload);

  // const handleClickDeleteScheduleAllDialogOpen = () => {
  //   setDeleteScheduleAllDialogOpen(true);
  // };

  const handleClickDeleteScheduleAllDialogClose = () => {
    setDeleteScheduleAllDialogOpen(false);
  };

  const handleClickSearchTabItem = (key: string) => {
    setSelectedSearchTabStatus(key);
    setSearchKeyword("");
  };

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchKeyword = event.target.value;
    setSearchKeyword(newSearchKeyword);
  };

  const handleChangePage = (selectedItem: { selected: number }) => {
    const page = selectedItem.selected;
    searchSchedules(page);
  };

  const searchSchedules = async (page: number = 0) => {
    dispatch(startGlobalLoadingAction());
    const payload: any = {
      page,
      fromDate: dateFormat(dateRange[0].startDate),
      toDate: dateFormat(dateRange[0].endDate),
    };

    if (selectedSearchTabStatus.length && searchKeyword !== "") {
      payload[selectedSearchTabStatus] = searchKeyword;
    }
    await setPagePayload(payload);
    await queryClient.invalidateQueries(ScheduleQueryKey.Page);
  };

  const debouncedSearchSchedules = useDebouncedCallback(
    async (page: number = 0) => {
      await searchSchedules(page)
    }
  );

  const handleScheduleDeleteEvent = async () => {
    await queryClient.invalidateQueries(ScheduleQueryKey.Page);
  };

  useEventBus(EventTypes.SCHEDULE_DELETE, handleScheduleDeleteEvent);

  useDidMountEffect(() => {
    debouncedSearchSchedules();
    EventBus.emit(EventTypes.PAGE_CLEAR);
  }, [searchKeyword]);

  useEffect(() => {
    searchSchedules();
    EventBus.emit(EventTypes.PAGE_CLEAR);
  }, [dateRange]);

  return (
    <ScheduleCheckContainer>
      <ScheduleCheckTitleContainer>스케줄 조회</ScheduleCheckTitleContainer>
      <ScheduleCheckHeaderContainer>
        <HeaderLeftContainer>
          <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
          <Divider />
          <SelectorContainer>
            <BasicSingleSelector
              value={searchTabMap[selectedSearchTabStatus]}
              defaultKey={"프로님"}
              candidateMap={searchTabMap}
              onClickSelectItem={handleClickSearchTabItem}
              width="7.063rem"
            />
          </SelectorContainer>
          <IconTextField
            placeholder={placeholderMap[selectedSearchTabStatus]}
            name={selectedSearchTabStatus}
            value={searchKeyword}
            onChange={handleChangeTextField}
            width="451px"
          />
        </HeaderLeftContainer>
        {/*<HeaderRightContainer>*/}
        {/*  {profile?.company.name === "SUPERUSER" && (*/}
        {/*    <ContainedRedButtonContainer>*/}
        {/*      <ContainedRedButton*/}
        {/*        width="8.188rem"*/}
        {/*        height="3.188rem"*/}
        {/*        text={"전체 삭제"}*/}
        {/*        onClick={handleClickDeleteScheduleAllDialogOpen}*/}
        {/*        sx={{*/}
        {/*          fontWeight: "500",*/}
        {/*          fontSize: "1rem",*/}
        {/*          lineHeight: "1.188rem",*/}
        {/*        }}*/}
        {/*      />*/}
        {/*    </ContainedRedButtonContainer>*/}
        {/*  )}*/}
        {/*</HeaderRightContainer>*/}
      </ScheduleCheckHeaderContainer>

      <ContentContainer>
        <SearchSchedulesInPeriodTable
            dateRange={dateRange}
        />
        {schedulePage.contents.length !== 0 ? <PageController
            basePage={schedulePage}
            onPageChange={handleChangePage}
        /> : ""}
      </ContentContainer>
      <DeliveryGroupDialog />

      {deleteScheduleAllDialogOpen && (
        <DeleteScheduleAllDialogContainer
          handleClickDialogClose={handleClickDeleteScheduleAllDialogClose}
        />
      )}
    </ScheduleCheckContainer>
  );
};

const ScheduleCheckContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`;

const ScheduleCheckTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 5%;

  color: ${theme.palette.primitives.black};
  font-size: 1.25rem;
  font-family: "Pretendard";
  font-style: normal;
  font-weight: 700;
  line-height: 1.5rem;
  padding: 1.5rem 0;
`;

const ScheduleCheckHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-bottom: 1.25rem;
  margin-left: 5%;
`;

const Divider = styled.div`
  width: 1px;
  height: 2.125rem;
  margin: 0 1rem;
  background: ${theme.palette.primitives.gray200};
`;

const SelectorContainer = styled.div`
  margin-right: 0.5rem;
`;

const ContentContainer = styled.div`
  height: 100%;
  background-color: ${theme.palette.primitives.gray50};
  padding-bottom: 10px;
  overflow-y: scroll;
`;

const ContainedRedButtonContainer = styled.div`
  margin-right: 0.75rem;
`;

const HeaderLeftContainer = styled.div`
  display: flex;
`;

const HeaderRightContainer = styled.div`
  display: flex;
  margin-right: 5%;
`;
